// Implements: F-UIX-020, F-UIX-008, F-UIX-009, F-UIX-010, F-DSG-006
/**
 * The logo and reference images of one stored profile, and their uploads.
 *
 * Owner round 4: the editor places the two parts apart (the logo's drop zone in the form, the
 * reference tiles behind a disclosure), so the container hands them to its child as `parts`; one
 * state and one lock serve both.
 *
 * The parent mounts it inside the editor, which is keyed by profile id
 * (checklist 14), so a file or an alt text chosen for profile A can never be
 * sent to profile B, and an upload that answers after the person moved on has
 * no container left to write into.
 *
 * Before anything is sent, the slot's alt text and file are judged with the
 * server's own rules (`referenceRules.ts`), and only where the server would
 * certainly refuse; everything else is the server's to judge, and its refusal
 * (size, type, pixels, the set) is shown inside the slot that was sent, with
 * what the person typed kept. One upload at a time: each one saves a version,
 * and two at once would race for the same next number.
 */

import { useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ProfileImageView } from "../../../types/design";
import { assetPath } from "../api/designApi";
import { LogoDrop, ReferenceGrid } from "../components/ReferenceUploader";
import type { DesignKey } from "../components/editorOptions";
import type { ReferenceSlot } from "../components/ReferenceUploader";
import { useUploadAsset } from "../hooks/useDesignProfiles";
import { ASSET_KEYS, altTextProblem, readReferenceFile } from "../model/referenceRules";
import type { AltTextProblem, AssetKey, ReferenceFileRefusal } from "../model/referenceRules";

/**
 * The upload refusals whose sentence the server writes for the person: a lost version race, and
 * a body over the request limit.
 */
const UPLOAD_REFUSAL_CODES = ["CONFLICT", "PAYLOAD_TOO_LARGE"] as const;

const FILE_REFUSALS: Readonly<Record<ReferenceFileRefusal, DesignKey>> = {
  notSupported: "design.references.refusal.notSupported",
  tooBig: "design.references.refusal.tooBig",
  unreadable: "design.references.refusal.unreadable",
};

/** The logo's own sentences (mockup v3 P5): it takes an SVG too, so its type refusal names four types. */
const LOGO_REFUSALS: Readonly<Record<ReferenceFileRefusal, DesignKey>> = {
  notSupported: "design.logo.badType",
  tooBig: "design.logo.tooBig",
  unreadable: "design.references.refusal.unreadable",
};

function refusalOf(key: AssetKey, reason: ReferenceFileRefusal): DesignKey {
  return key === "logo" ? LOGO_REFUSALS[reason] : FILE_REFUSALS[reason];
}

const ALT_PROBLEMS: Readonly<Record<AltTextProblem, DesignKey>> = {
  required: "design.references.altRequired",
  tooLong: "design.references.altTooLong",
};

export interface ReferenceUploaderContainerProps {
  readonly profileId: string;
  /** The profile's name: the alt text a dropped logo is uploaded with until the person changes it. */
  readonly profileName: string;
  /** The version the images belong to: the latest stored one. */
  readonly version: number;
  readonly images: readonly ProfileImageView[];
  /** Where the parts go: the logo's drop zone, the reference tiles, and how many references are stored. */
  readonly children: (parts: UploaderParts) => ReactNode;
}

export interface UploaderParts {
  readonly logo: ReactElement;
  readonly references: ReactElement;
  readonly count: number;
}

interface SlotDraft {
  readonly file: File | null;
  readonly alt: string;
  readonly fileError?: DesignKey | undefined;
  readonly altError?: DesignKey | undefined;
  readonly refusal?: string | undefined;
}

/** A slot with nothing chosen and nothing refused, which is also what a stored upload leaves. */
const EMPTY: SlotDraft = {
  file: null,
  alt: "",
  fileError: undefined,
  altError: undefined,
  refusal: undefined,
};

export function ReferenceUploaderContainer({
  profileId,
  profileName,
  version,
  images,
  children,
}: ReferenceUploaderContainerProps): ReactElement {
  const { t } = useTranslation();
  const upload = useUploadAsset();
  const [drafts, setDrafts] = useState<Partial<Record<AssetKey, SlotDraft>>>({});
  const [pendingKey, setPendingKey] = useState<AssetKey | null>(null);
  const [uploaded, setUploaded] = useState<{ readonly key: AssetKey; readonly version: number } | null>(null);

  const draftOf = (key: AssetKey): SlotDraft => drafts[key] ?? EMPTY;
  // Functional updates: an answer lands after other slots may have changed.
  const patch = (key: AssetKey, change: Partial<SlotDraft>): void => {
    setDrafts((current) => ({ ...current, [key]: { ...(current[key] ?? EMPTY), ...change } }));
  };

  /**
   * Upload one slot. `given` is a draft not yet in state (a drop); `keep` leaves its file and alt
   * text in the slot after a stored upload, so the alt text of a dropped logo stays editable.
   */
  const send = async (key: AssetKey, given?: SlotDraft, keep = false): Promise<void> => {
    if (pendingKey !== null) return;
    const draft = given ?? draftOf(key);
    if (draft.file === null) return;
    const problem = altTextProblem(draft.alt);
    setPendingKey(key);
    setUploaded(null);
    const read = await readReferenceFile(draft.file, { svg: key === "logo" });
    const fileError = read.kind === "refused" ? refusalOf(key, read.reason) : undefined;
    const altError = problem === null ? undefined : ALT_PROBLEMS[problem];
    if (read.kind === "refused" || altError !== undefined) {
      setPendingKey(null);
      patch(key, { fileError, altError, refusal: undefined });
      return;
    }
    const outcome = await upload(profileId, { key, alt_text: draft.alt, data: read.base64 });
    setPendingKey(null);
    if (!outcome.ok) {
      patch(key, {
        fileError: undefined,
        altError: undefined,
        refusal: userMessage(outcome.error, t("design.references.failed"), UPLOAD_REFUSAL_CODES),
      });
      return;
    }
    patch(key, keep ? { ...EMPTY, file: draft.file, alt: draft.alt } : EMPTY);
    const version = outcome.uploaded?.version;
    setUploaded(version === undefined ? null : { key, version });
  };

  const slotOf = (key: AssetKey): ReferenceSlot => {
    const stored = images.find((image) => image.key === key);
    const draft = draftOf(key);
    return {
      key,
      image:
        stored === undefined
          ? null
          : { src: assetPath(profileId, version, stored.asset_id), alt: stored.alt_text },
      fileName: draft.file?.name ?? null,
      alt: draft.alt,
      fileError: draft.fileError === undefined ? undefined : t(draft.fileError),
      altError: draft.altError === undefined ? undefined : t(draft.altError),
      refusal: draft.refusal,
    };
  };

  const announce = (logoPart: boolean): string | null =>
    uploaded === null || (uploaded.key === "logo") !== logoPart
      ? null
      : t("design.references.uploaded", { version: uploaded.version });
  const actions = {
    pendingKey,
    onFile: (key: AssetKey, file: File) => {
      patch(key, { file, fileError: undefined, refusal: undefined });
      // The logo says at once when a dropped or chosen file can never be stored (type or size);
      // the answer is ignored if another file was chosen meanwhile (checklist 14).
      if (key !== "logo") return;
      void readReferenceFile(file, { svg: true }).then((read) => {
        if (read.kind === "ok") return;
        setDrafts((current) =>
          current[key]?.file === file ? { ...current, [key]: { ...(current[key] ?? EMPTY), fileError: refusalOf(key, read.reason) } } : current,
        );
      });
    },
    onAltChange: (key: AssetKey, alt: string) => {
      patch(key, { alt, altError: undefined });
    },
    onUpload: (key: AssetKey) => {
      void send(key, undefined, key === "logo" && draftOf(key).file !== null);
    },
    onDrop: (key: AssetKey, file: File) => {
      // Mockup v3: a dropped logo is uploaded at once, with the person's alt text or the profile's name.
      const typed = draftOf(key).alt.trim();
      const draft: SlotDraft = { ...EMPTY, file, alt: typed === "" ? profileName : draftOf(key).alt };
      patch(key, draft);
      void send(key, draft, true);
    },
  };
  return (
    <>
      {children({
        logo: <LogoDrop slot={slotOf("logo")} {...actions} uploaded={announce(true)} />,
        references: <ReferenceGrid slots={ASSET_KEYS.filter((key) => key !== "logo").map(slotOf)} {...actions} uploaded={announce(false)} />,
        count: images.filter((image) => image.key !== "logo").length,
      })}
    </>
  );
}
